import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Row, Col, Button, Form, Pagination } from 'react-bootstrap';
import { default as Selector } from 'react-select';
import { toast } from 'react-toastify';
import { FaSearch, FaFileExcel, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles } from '~/Styles/reactSelectStyles';
import { exportarEmExcel } from '~/Utils/Helpers';
import * as api from '~/Services/Service';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

// W-0584 — GES > Relatórios > Ativos > Ativo por Família. Read-only.
// Fiel a URelAtivoFamilia.pas: filtro família (>=1) + status (0/1/Todos) + saldo (COM/SEM/Ambos).

const COLUNAS = [
  { campo: 'codigo_familia', titulo: 'Cód. Família', tipo: 'int' },
  { campo: 'codigo_ativo', titulo: 'Cód. Ativo', tipo: 'int' },
  { campo: 'nome', titulo: 'Nome', tipo: 'texto' },
  { campo: 'unidade', titulo: 'Unidade', tipo: 'texto' },
  { campo: 'custo_medio', titulo: 'Custo Médio', tipo: 'decimal' },
  { campo: 'preco_base', titulo: 'Preço Base', tipo: 'decimal' },
  { campo: 'preco_padrao', titulo: 'Preço Padrão', tipo: 'decimal' },
  { campo: 'nome_da_familia', titulo: 'Família', tipo: 'texto' },
];

const ITENS_POR_PAGINA = 20;

const formatarDecimal = valor => {
  if (valor === null || valor === undefined || valor === '') return '0,00';
  return Number(valor).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const RelatorioAtivoFamilia = () => {
  const [familiasOptions, setFamiliasOptions] = useState([]);
  const [familiasSelecionadas, setFamiliasSelecionadas] = useState([]);
  const [status, setStatus] = useState('0'); // default Ativo (r3 do .pas)
  const [saldo, setSaldo] = useState(''); // default Ambos (RadioButton3 do .pas)

  const [registros, setRegistros] = useState([]);
  const [pesquisou, setPesquisou] = useState(false);
  const [carregando, setCarregando] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    carregarFamilias();
  }, []);

  const carregarFamilias = async () => {
    try {
      const res = await api.listarFamiliasAtivos(null, null, null, null);
      const lista = res || [];
      setFamiliasOptions(
        lista.map(f => ({
          value: f.codigo,
          label: f.codigo + ' - ' + f.nome,
        }))
      );
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao carregar famílias.');
    }
  };

  const registrosOrdenados = useMemo(() => {
    if (!sortConfig.key) return registros;
    const copia = [...registros];
    copia.sort((a, b) => {
      const va = a[sortConfig.key];
      const vb = b[sortConfig.key];
      if (va < vb) return sortConfig.direction === 'asc' ? -1 : 1;
      if (va > vb) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return copia;
  }, [registros, sortConfig]);

  const ordenar = campo => {
    let direction = 'asc';
    if (sortConfig.key === campo && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: campo, direction });
    setPagina(1);
  };

  const iconeOrdenacao = campo => {
    if (sortConfig.key !== campo) {
      return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    }
    return sortConfig.direction === 'asc' ? (
      <FaSortUp style={{ marginLeft: 4 }} />
    ) : (
      <FaSortDown style={{ marginLeft: 4 }} />
    );
  };

  const gerarRelatorio = async () => {
    // RN-1 — pelo menos 1 família obrigatória; não chamar a API sem família.
    if (!familiasSelecionadas || familiasSelecionadas.length === 0) {
      toast.error('Selecione pelo menos uma família.');
      return;
    }

    const filtro = {
      familias: familiasSelecionadas.map(f => f.value),
      status: status === '' ? null : status,
      saldo: saldo === '' ? null : saldo,
    };

    try {
      setCarregando(true);
      const res = await api.listarAtivoFamilia(filtro);
      setRegistros(res || []);
      setPagina(1);
      setPesquisou(true);
      setSortConfig({ key: null, direction: 'asc' });
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro inesperado');
    } finally {
      setCarregando(false);
    }
  };

  const exportarExcel = () => {
    if (!registrosOrdenados || registrosOrdenados.length === 0) return;
    const cabecalho = COLUNAS.map(c => c.titulo);
    const linhas = registrosOrdenados.map(item =>
      COLUNAS.map(c =>
        c.tipo === 'decimal' ? formatarDecimal(item[c.campo]) : item[c.campo]
      )
    );
    exportarEmExcel([cabecalho, ...linhas], 'ativo-por-familia.csv');
  };

  // Paginação client-side (mesmo padrão de ControleContratos).
  const totalPaginas = Math.ceil(registrosOrdenados.length / ITENS_POR_PAGINA) || 1;
  const registrosPaginados = registrosOrdenados.slice(
    (pagina - 1) * ITENS_POR_PAGINA,
    pagina * ITENS_POR_PAGINA
  );

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const itens = [];
    const maxBotoes = 5;

    itens.push(
      <Pagination.First
        key="first"
        onClick={() => setPagina(1)}
        disabled={pagina === 1}
      />
    );
    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPagina(pagina - 1)}
        disabled={pagina === 1}
      />
    );

    let inicioPaginas = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    const fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let i = inicioPaginas; i <= fimPaginas; i += 1) {
      itens.push(
        <Pagination.Item
          key={i}
          active={i === pagina}
          onClick={() => setPagina(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next
        key="next"
        onClick={() => setPagina(pagina + 1)}
        disabled={pagina === totalPaginas}
      />
    );
    itens.push(
      <Pagination.Last
        key="last"
        onClick={() => setPagina(totalPaginas)}
        disabled={pagina === totalPaginas}
      />
    );

    return (
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{ borderTop: '1px solid #dee2e6' }}
      >
        <small className="text-muted">
          {`Mostrando ${
            registrosPaginados.length > 0
              ? (pagina - 1) * ITENS_POR_PAGINA + 1
              : 0
          } até ${Math.min(
            pagina * ITENS_POR_PAGINA,
            registrosOrdenados.length
          )} de ${registrosOrdenados.length} registros`}
        </small>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  return (
    <div>
      <Header title="Ativo por Família" />
      <Container fluid className="mt-3">
        <Card className="mb-3">
          <Card.Body>
            <Row>
              <Col xs={12} md={6} lg={5}>
                <Form.Group>
                  <Form.Label>Famílias</Form.Label>
                  <Selector
                    isMulti
                    styles={customStyles}
                    placeholder="Selecione uma ou mais famílias..."
                    noOptionsMessage={() => 'Nenhuma família encontrada'}
                    options={familiasOptions}
                    value={familiasSelecionadas}
                    onChange={selecionadas =>
                      setFamiliasSelecionadas(selecionadas || [])
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3} lg={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      id="status-ativo"
                      name="status"
                      label="Ativo"
                      checked={status === '0'}
                      onChange={() => setStatus('0')}
                    />
                    <Form.Check
                      type="radio"
                      id="status-inativo"
                      name="status"
                      label="Inativo"
                      checked={status === '1'}
                      onChange={() => setStatus('1')}
                    />
                    <Form.Check
                      type="radio"
                      id="status-todos"
                      name="status"
                      label="Todos"
                      checked={status === ''}
                      onChange={() => setStatus('')}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col xs={12} md={3} lg={4}>
                <Form.Group>
                  <Form.Label>Saldo</Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      id="saldo-com"
                      name="saldo"
                      label="Com saldo conta 1"
                      checked={saldo === 'COM'}
                      onChange={() => setSaldo('COM')}
                    />
                    <Form.Check
                      type="radio"
                      id="saldo-sem"
                      name="saldo"
                      label="Sem saldo"
                      checked={saldo === 'SEM'}
                      onChange={() => setSaldo('SEM')}
                    />
                    <Form.Check
                      type="radio"
                      id="saldo-ambos"
                      name="saldo"
                      label="Ambos"
                      checked={saldo === ''}
                      onChange={() => setSaldo('')}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-2">
              <Button
                variant="success"
                disabled={carregando}
                onClick={gerarRelatorio}
              >
                <FaSearch />
                <span className="ml-2">Gerar Relatório</span>
              </Button>
              <Button
                variant="outline-success"
                className="ml-2"
                disabled={carregando || registrosOrdenados.length === 0}
                onClick={exportarExcel}
              >
                <FaFileExcel />
                <span className="ml-2">Exportar Excel</span>
              </Button>
            </div>
          </Card.Body>
        </Card>

        {carregando && <Loading />}

        {!carregando && pesquisou && (
          <Card>
            <Card.Body className="table-responsive">
              {registrosOrdenados.length === 0 && (
                <div className="text-center text-muted py-3">
                  Nenhum registro encontrado
                </div>
              )}
              {registrosOrdenados.length > 0 && (
                <>
                <Table className="table table-striped table-bordered table-hover table-sm">
                  <Thead>
                    <Tr className="text-center">
                      {COLUNAS.map(c => (
                        <Th
                          key={c.campo}
                          onClick={() => ordenar(c.campo)}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          {c.titulo}
                          {iconeOrdenacao(c.campo)}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody className="text-center">
                    {registrosPaginados.map((item, indice) => (
                      <Tr key={`${(pagina - 1) * ITENS_POR_PAGINA + indice}-${item.codigo_ativo}`}>
                        <Td>{item.codigo_familia}</Td>
                        <Td>{item.codigo_ativo}</Td>
                        <Td>{item.nome}</Td>
                        <Td>{item.unidade}</Td>
                        <Td>{formatarDecimal(item.custo_medio)}</Td>
                        <Td>{formatarDecimal(item.preco_base)}</Td>
                        <Td>{formatarDecimal(item.preco_padrao)}</Td>
                        <Td>{item.nome_da_familia}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {renderizarPaginacao()}
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default RelatorioAtivoFamilia;
